import { useScoreStore } from '@assets/model/ScoreUtils';
import { useConfig } from '@assets/model/StoreConfig';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import {
    Database,
    getDatabase,
    onValue,
    push,
    ref,
    serverTimestamp,
    set,
    update
} from 'firebase/database';
import { over } from 'lodash';
import { useStore, useStoreUI } from 'src/model/Store';

const PATH_RAG = '/Service_RAG';
const PATH_CHAT = '/Service_Chat';
const PATH_RAG_REMOTE_ACTION = 'Service_RAG_RemoteAction';

interface FileUpload {
    url: string;
    name: string;
    thumbnail: string;
}

type AuditEntryType =
    | 'LOGIN'
    | 'APP_LOAD'
    | 'VIEW_PAGE_DEAL'
    | 'VIEW_PAGE_DB'
    | 'EDIT_FIELD'
    | 'ADD_NEW_DEAL'
    | 'DOWNLOAD_CIM'
    | 'UPLOAD_CIM'
    | 'DELETE_DEAL';

const PATH_DB_SERVICE_FILES = '/Service_Files';

function sanitizeName(name) {
    const validChars = new Set(
        '-_()ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    );
    return [...name].filter(c => validChars.has(c)).join('');
}

function dbBuildProjectFilePath(reportId: string, fileName: string) {
    const noExt = fileName.replace(/\.[^/.]+$/, '');
    const entryName = sanitizeName(noExt);
    return `${PATH_DB_SERVICE_FILES}/${reportId}/files/${entryName}`;
}

class FirebaseAdapater {
    app: FirebaseApp;
    db: Database;
    userId: string;
    user: User = null;

    getConfig() {
        return useConfig().firebase;
    }

    init() {
        const firebaseConfig = this.getConfig();
        console.log('Initializing Firebase');
        console.log(firebaseConfig);
        console.log('Is Developer: ', useStoreUI().isDev);

        this.app = initializeApp(firebaseConfig);
        this.db = getDatabase(this.app);
        var auth = getAuth(this.app);

        onAuthStateChanged(auth, user => {
            if (user) {
                console.log('Logged in!');
                this.userId = user.uid;
                this.user = user;

                const store = useStore();
                store.userId = user.uid;

                //TODO: Clean this up a bit
                this.store_audit_entry('APP_LOAD');

                var r = ref(this.db, '/cim_results');
                onValue(r, data => {
                    const val = data.val();
                    //console.log(JSON.stringify(val));
                    const store = useStore();
                    store.resetDataAccessCache();
                    store.reportDataDB = val || {};
                });

                var r_overrides = ref(this.db, '/cim_results_overrides');
                onValue(r_overrides, data => {
                    const val = data.val();
                    //console.log(JSON.stringify(val));
                    const store = useStore();
                    store.resetDataAccessCache();

                    store.reportDataOverridesDB = val || {};
                    store.areOveridesLoaded = true;
                });

                var r_firminfo = ref(this.db, '/firm_info');
                onValue(r_firminfo, data => {
                    const val = data.val();
                    //console.log(JSON.stringify(val));
                    const store = useStore();
                    store.firmInfo = val || {};
                });

                var r_project_files = ref(this.db, PATH_DB_SERVICE_FILES);
                onValue(r_project_files, data => {
                    const val = data.val();
                    const store = useStore();
                    for (var reportId in val) {
                        const files = val[reportId].files;
                        if (files) {
                            for (var key in files) {
                                files[key].saveKey = key;
                            }
                        }
                    }

                    store.allProjectFiles = val || {};
                });

                var r_rag = ref(this.db, PATH_RAG);
                onValue(r_rag, data => {
                    var val = data.val();
                    //console.log(JSON.stringify(val));
                    const store = useStore();
                    // for (var key in val) {
                    //     val[key]['originalDocId'] = key;
                    // }

                    store.ragDB = val || {};
                    store.isRagDbLoaded = true;
                });

                var r_settings = ref(this.db, '/settings');
                onValue(r_settings, data => {
                    const val = data.val();
                    //console.log(JSON.stringify(val));
                    const store = useStore();
                    store.settings = val || {};
                });

                var r_scoreoverride = ref(this.db, '/score_override');
                onValue(r_scoreoverride, data => {
                    const val = data.val();
                    //console.log(JSON.stringify(val));
                    const ruleStore = useScoreStore();
                    ruleStore.update_rules(val || {});

                    const store = useStore();
                    store.areScoreRulesLoaded = true;
                });

                var r = ref(this.db, '/jobs');
                onValue(r, data => {
                    const store = useStore();
                    store.jobs = data.val();
                });

                var chatsRef = ref(this.db, PATH_CHAT);
                onValue(chatsRef, data => {
                    const store = useStore();

                    store.chatData = data.val() ?? {};
                    store.isChatLoaded = true;
                });

                var activeJobs = ref(this.db, '/activeJob');
                onValue(activeJobs, data => {
                    const store = useStore();
                    store.activeJob = data.val();
                });

                var users = ref(this.db, '/users');
                onValue(users, data => {
                    const store = useStore();
                    store.users = data.val();
                });

                // var onlineStatus = ref(this.db, '/system_status');
                // onValue(onlineStatus, data => {
                //     store.systemStatus = data.val();
                // });
            } else {
                if (location.href.indexOf('login') < 0) {
                    location.href = '/login';
                }
            }
        });
    }

    deleteReport(reportId: string) {
        this.store_audit_entry('DELETE_DEAL', {
            reportId
        });

        var r = ref(this.db, `/cim_results/${reportId}`);
        set(r, null);
    }

    store_override(reportId: string, overidePath: string, value: any) {
        const path = `cim_results_overrides/${reportId}/${overidePath}`;
        const switchDot = path.replace('.', '___');
        const r = ref(this.db, switchDot);
        set(r, value);

        this.store_audit_entry('EDIT_FIELD', {
            reportId: reportId,
            field: overidePath,
            value: value
        });
    }

    remove_override(reportId: string, overidePath: string) {
        const path = `cim_results_overrides/${reportId}/${overidePath}`;
        const switchDot = path.replace('.', '___');
        const r = ref(this.db, switchDot);
        set(r, null);

        this.store_audit_entry('EDIT_FIELD', {
            reportId: reportId,
            field: overidePath,
            value: 'DELETED'
        });
    }

    store_audit_entry(type: AuditEntryType, extraData: any = {}) {
        if (!this.userId) {
            setTimeout(() => {
                this.store_audit_entry(type, extraData);
            }, 1000);
            return;
        }
        const userId = this.userId;
        const userEmail = this.user.email;

        const path = `audit_log`;
        const r = ref(this.db, path);

        push(r, {
            type: type,
            userId: userId,
            userEmail: userEmail,
            extraData: extraData,
            date: serverTimestamp()
        });
    }

    addDummyProjectFile(reportId: string, fileName: string) {
        const path = dbBuildProjectFilePath(reportId, fileName);
        const r = ref(this.db, path);
        update(r, {
            filename: fileName
        });
    }

    deleteProjectFile(reportId: string, filename: string) {
        const path = dbBuildProjectFilePath(reportId, filename);
        const r = ref(this.db, path);
        set(r, null);
    }

    triggerScanRagFiles() {
        const r = ref(this.db, PATH_RAG_REMOTE_ACTION);

        set(r, {
            action: 'import',
            started: false
        });
    }

    clearJobQueue() {
        const r = ref(this.db, '/jobs');
        set(r, null);
    }

    setJobStatus(jobId: string, status: string) {
        const r = ref(this.db, `/jobs/${jobId}`);
        update(r, {
            status: status
        });
    }

    add_job(
        fileName: string,
        password?: string,
        originalJobId?: string,
        clearCache?: boolean,
        priority?: number
    ) {
        const r = ref(this.db, `/jobs`);
        const data = {
            fileName: fileName,
            user: getAuth().currentUser.email,
            userId: getAuth().currentUser.uid,
            priority: priority || 0,
            status: 'pending',
            createdAt: serverTimestamp()
        };

        if (password) {
            data['pdfPassword'] = password;
        }

        if (originalJobId) {
            data['originalJobId'] = originalJobId;
        }

        if (clearCache) {
            data['clearCache'] = true;
        }

        push(r, data);
    }

    chatbot_add_message(reportId: string, message: string, isMe: boolean) {
        const r = ref(this.db, `${PATH_CHAT}/${reportId}`);
        push(r, {
            message,
            isMe,
            processed: false,
            date: serverTimestamp()
        });
    }

    clear_chat(reportId: string) {
        const r = ref(this.db, `${PATH_CHAT}/${reportId}`);
        set(r, null);
    }
}

const mFirebaseService = new FirebaseAdapater();
export function getFirebase() {
    return mFirebaseService;
}
